import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { PosStoreService } from '@adminpages/pos-state';
import { PosPrintingService } from '@adminpages/pos-printing';
import { CommercialOrderSource, RecentPosOrder } from '@adminpages/domain-models';

@Component({
  selector: 'foco-pos-recent-orders-page',
  standalone: true,
  imports: [FormsModule, RouterLink, IonButton, IonButtons, IonCheckbox, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonModal, IonText, IonTitle, IonToolbar],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button routerLink="/pos">
            <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>Ventas recientes</ion-title>
        <ion-buttons slot="end">
          <ion-button routerLink="/cash-session" [queryParams]="{ scope: 'pos', return: '/pos/recent' }">
            <ion-icon name="cash-outline" slot="start"></ion-icon>
            Caja
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="pos-page recent-content">
      <main class="recent-shell">
        <section class="summary-row">
          <article>
            <span>Ventas hoy</span>
            <strong>{{ store.recentOrders().length }}</strong>
          </article>
          <article>
            <span>Cobrado</span>
            <strong>{{ store.money(confirmedTotal()) }}</strong>
          </article>
          <article>
            <span>Saldos</span>
            <strong>{{ store.money(pendingBalance()) }}</strong>
          </article>
        </section>

        <section class="source-filter">
          <span>Origen</span>
          <label>
            <ion-checkbox [checked]="sourceEnabled('pos')" (ionChange)="toggleSource('pos', $event.detail.checked)"></ion-checkbox>
            POS
          </label>
          <label>
            <ion-checkbox [checked]="sourceEnabled('ecommerce')" (ionChange)="toggleSource('ecommerce', $event.detail.checked)"></ion-checkbox>
            Ecommerce
          </label>
          <label>
            <ion-checkbox [checked]="sourceEnabled('panel')" (ionChange)="toggleSource('panel', $event.detail.checked)"></ion-checkbox>
            Panel
          </label>
        </section>

        @if (store.recentLoading()) {
          <section class="orders-message">Cargando ventas...</section>
        }

        @if (store.errorMessage(); as error) {
          <section class="orders-message error">{{ error }}</section>
        }

        <section class="orders-list">
          @for (order of store.recentOrders(); track order.uuid) {
            <article class="order-card" (click)="openDetail(order)">
              <div>
	                <strong>{{ order.folio }}</strong>
	                <span>{{ sourceLabel(order.source) }} · {{ order.customerName }} · {{ order.createdAt }}</span>
              </div>
              <div class="order-status">
                <span class="status-pill" [class.success]="order.paymentStatus === 'paid'" [class.warning]="order.paymentStatus === 'partial'" [class.neutral]="order.paymentStatus === 'unpaid'">
                  {{ paymentLabel(order.paymentStatus) }}
                </span>
                <small>{{ statusLabel(order.status) }}</small>
              </div>
              <div class="order-money">
                <strong>{{ store.money(order.total) }}</strong>
                <small>Saldo {{ store.money(order.balanceDue) }}</small>
                @if (order.balanceDue > 0) {
                  <ion-button size="small" (click)="openPayment(order); $event.stopPropagation()">
                    Cobrar saldo
                  </ion-button>
                }
              </div>
            </article>
          }
        </section>
      </main>
    </ion-content>

    <ion-modal cssClass="order-detail-modal" [isOpen]="detailOpen()" (didDismiss)="detailOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Detalle venta</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="detailOpen.set(false)">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>

        <ion-content class="detail-content">
          @if (detailOrder(); as order) {
            <section class="detail-hero">
              <span>{{ sourceLabel(order.source) }} · {{ statusLabel(order.status) }}</span>
              <h2>{{ order.folio }}</h2>
              <strong>{{ store.money(order.total) }}</strong>
            </section>

            <section class="detail-grid">
              <article>
                <span>Cliente</span>
                <strong>{{ order.customerName }}</strong>
                @if (order.customerPhone) {
                  <small>{{ order.customerPhone }}</small>
                }
                @if (order.customerEmail) {
                  <small>{{ order.customerEmail }}</small>
                }
              </article>
              <article>
                <span>Pagado</span>
                <strong>{{ store.money(order.paidTotal) }}</strong>
                <small>Saldo {{ store.money(order.balanceDue) }}</small>
              </article>
            </section>

            <section class="detail-section">
              <header>
                <span>Partidas</span>
              </header>
              @for (item of order.items; track item.id) {
                <article class="detail-row">
                  <div>
                    <strong>{{ item.name }}</strong>
                    <small>{{ item.quantity }} x {{ store.money(item.unitPrice) }}</small>
                    @if (item.addons.length > 0) {
                      <small>
                        @for (addon of item.addons; track addon.name) {
                          + {{ addon.name }}
                        }
                      </small>
                    }
                  </div>
                  <strong>{{ store.money(item.total) }}</strong>
                </article>
              } @empty {
                <ion-text color="medium">Sin partidas.</ion-text>
              }
            </section>

            <section class="detail-section">
              <header>
                <span>Pagos</span>
              </header>
              @for (payment of order.payments; track payment.id) {
                <article class="detail-row">
                  <div>
                    <strong>{{ methodLabel(payment.method) }}</strong>
                    <small>{{ paymentLabel(order.paymentStatus) }} · {{ payment.type }}</small>
                    @if (payment.reference) {
                      <small>{{ payment.reference }}</small>
                    }
                  </div>
                  <strong>{{ store.money(payment.amount) }}</strong>
                </article>
              } @empty {
                <ion-text color="medium">Sin pagos registrados.</ion-text>
              }
            </section>
          }
        </ion-content>

        @if (detailOrder(); as order) {
          <ion-footer>
            <ion-toolbar>
              <div class="detail-actions">
                <ion-button expand="block" fill="outline" size="large" (click)="printReceipt(order)">
                  <ion-icon name="receipt-outline" slot="start"></ion-icon>
                  Recibo
                </ion-button>
                @if (order.balanceDue > 0) {
                <ion-button expand="block" size="large" (click)="openPaymentFromDetail(order)">
                  Cobrar saldo
                </ion-button>
                }
              </div>
            </ion-toolbar>
          </ion-footer>
        }
      </ng-template>
    </ion-modal>

    <ion-modal cssClass="checkout-modal" [isOpen]="paymentOpen()" (didDismiss)="paymentOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Cobro</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="paymentOpen.set(false)">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content class="payment-content">
          @if (selectedOrder(); as order) {
            <section class="payment-total">
              <span>{{ order.folio }}</span>
              <strong>{{ store.money(order.balanceDue) }}</strong>
              <small>Saldo pendiente</small>
            </section>

            <section class="payment-methods">
              <button type="button" [class.selected]="paymentMethod() === 'cash'" (click)="paymentMethod.set('cash')">
                <ion-icon name="cash-outline"></ion-icon>
                Efectivo
              </button>
              <button type="button" [class.selected]="paymentMethod() === 'transfer'" (click)="paymentMethod.set('transfer')">
                <ion-icon name="card-outline"></ion-icon>
                Transferencia
              </button>
            </section>

            <ion-item>
              <ion-label position="stacked">Monto recibido</ion-label>
              <ion-input type="number" [ngModel]="paymentAmount()" (ngModelChange)="paymentAmount.set(+$event)"></ion-input>
            </ion-item>
          }
        </ion-content>
        <ion-footer>
          <ion-toolbar>
            @if (store.errorMessage(); as error) {
              <ion-text color="danger">{{ error }}</ion-text>
            }
            <ion-button expand="block" size="large" [disabled]="store.checkoutLoading()" (click)="completePayment()">
              {{ store.checkoutLoading() ? 'Guardando...' : selectedOrder() && paymentAmount() >= selectedOrder()!.balanceDue ? 'Liquidar venta' : 'Registrar anticipo' }}
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </ng-template>
    </ion-modal>

    @if (printableOrder(); as order) {
      <section class="print-receipt" aria-hidden="true">
        <article class="receipt-paper">
          <header class="receipt-header">
            <strong>{{ receiptBusinessName() }}</strong>
            <span>{{ sourceLabel(order.source) }} · {{ statusLabel(order.status) }}</span>
            <span>{{ order.createdAt }}</span>
          </header>

          <section class="receipt-folio">
            <span>Folio</span>
            <strong>{{ order.folio }}</strong>
          </section>

          <section class="receipt-block">
            <span>Cliente</span>
            <strong>{{ order.customerName }}</strong>
            @if (order.customerPhone) {
              <small>{{ order.customerPhone }}</small>
            }
            @if (order.customerEmail) {
              <small>{{ order.customerEmail }}</small>
            }
          </section>

          <section class="receipt-items">
            <h2>Partidas</h2>
            @for (item of order.items; track item.id) {
              <div class="receipt-line">
                <div>
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.quantity }} x {{ store.money(item.unitPrice) }}</span>
                  @if (item.addons.length > 0) {
                    @for (addon of item.addons; track addon.name) {
                      <small>+ {{ addon.name }}{{ addonPrice(addon) > 0 ? ' · ' + store.money(addonPrice(addon)) : '' }}</small>
                    }
                  }
                </div>
                <strong>{{ store.money(item.total) }}</strong>
              </div>
            }
          </section>

          <section class="receipt-totals">
            <div>
              <span>Total</span>
              <strong>{{ store.money(order.total) }}</strong>
            </div>
            <div>
              <span>Pagado</span>
              <strong>{{ store.money(order.paidTotal) }}</strong>
            </div>
            <div>
              <span>Saldo</span>
              <strong>{{ store.money(order.balanceDue) }}</strong>
            </div>
          </section>

          <section class="receipt-payments">
            <h2>Pagos</h2>
            @for (payment of order.payments; track payment.id) {
              <div>
                <span>{{ methodLabel(payment.method) }} · {{ paymentStatusLabel(payment.status) }}</span>
                <strong>{{ store.money(payment.amount) }}</strong>
              </div>
            } @empty {
              <span>Sin pagos registrados.</span>
            }
          </section>

          <footer class="receipt-footer">
            <span>Gracias por tu compra.</span>
          </footer>
        </article>
      </section>
    }
  `,
  styles: `
    ion-toolbar {
      --background: var(--pos-color-surface);
    }

    .recent-content {
      --padding-bottom: calc(72px + env(safe-area-inset-bottom));
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .recent-shell {
      display: grid;
      gap: 18px;
      margin: 0 auto;
      max-width: 1040px;
      padding-bottom: 36px;
    }

    .summary-row {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .source-filter {
      align-items: center;
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      padding: 12px 14px;
    }

    .source-filter span {
      color: var(--pos-color-muted);
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .source-filter label {
      align-items: center;
      color: var(--pos-color-text);
      display: inline-flex;
      font-weight: 700;
      gap: 8px;
    }

    .source-filter ion-checkbox {
      --checkbox-background-checked: var(--pos-color-primary);
      --border-color-checked: var(--pos-color-primary);
      --checkmark-color: #fff;
    }

    .summary-row article,
    .order-card {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      box-shadow: 0 10px 28px var(--pos-color-shadow);
    }

    .summary-row article {
      display: grid;
      gap: 6px;
      padding: 18px;
    }

    .summary-row span,
    .order-card span,
    .order-card small {
      color: var(--pos-color-muted);
    }

    .summary-row strong {
      font-size: 28px;
    }

    .orders-list {
      display: grid;
      gap: 10px;
    }

    .orders-message {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      color: var(--pos-color-muted);
      padding: 14px;
    }

    .orders-message.error {
      color: var(--ion-color-danger);
    }

    .order-card {
      align-items: center;
      cursor: pointer;
      display: grid;
      gap: 12px;
      grid-template-columns: minmax(0, 1fr) auto auto;
      padding: 16px;
    }

    .order-card strong,
    .order-card span,
    .order-card small {
      display: block;
    }

    .order-status,
    .order-money {
      display: grid;
      gap: 6px;
      justify-items: end;
    }

    .order-money ion-button {
      margin: 0;
    }

    .payment-content {
      --padding-bottom: 18px;
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .detail-content {
      --padding-bottom: 18px;
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .detail-hero {
      background: var(--pos-color-input, rgba(0, 0, 0, 0.04));
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: grid;
      gap: 6px;
      margin-bottom: 14px;
      padding: 16px;
    }

    .detail-hero span,
    .detail-grid span,
    .detail-row small,
    .detail-section header span {
      color: var(--pos-color-muted);
    }

    .detail-hero h2 {
      margin: 0;
    }

    .detail-hero > strong {
      font-size: 32px;
    }

    .detail-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-bottom: 14px;
    }

    .detail-grid article,
    .detail-section {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: grid;
      gap: 10px;
      padding: 14px;
    }

    .detail-grid article {
      align-content: start;
    }

    .detail-grid strong,
    .detail-row strong,
    .detail-row small {
      display: block;
    }

    .detail-row {
      align-items: start;
      border-top: 1px solid var(--pos-color-border);
      display: grid;
      gap: 12px;
      grid-template-columns: minmax(0, 1fr) auto;
      padding-top: 10px;
    }

    .detail-row:first-of-type {
      border-top: 0;
      padding-top: 0;
    }

    .detail-actions {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      padding: 8px 12px;
    }

    .detail-actions ion-button:only-child {
      grid-column: 1 / -1;
    }

    .print-receipt {
      display: none;
    }

    .payment-total {
      background: var(--pos-color-input, rgba(0, 0, 0, 0.04));
      border-radius: 8px;
      display: grid;
      gap: 4px;
      margin-bottom: 16px;
      padding: 18px;
    }

    .payment-total span,
    .payment-total small {
      color: var(--pos-color-muted);
    }

    .payment-total strong {
      font-size: 34px;
    }

    .payment-methods {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-bottom: 16px;
    }

    .payment-methods button {
      align-items: center;
      background: transparent;
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      color: var(--pos-color-text);
      display: grid;
      font-weight: 700;
      gap: 8px;
      min-height: 92px;
      padding: 16px;
    }

    .payment-methods button.selected {
      border-color: var(--pos-color-primary);
      color: var(--pos-color-primary);
    }

    .payment-methods ion-icon {
      font-size: 28px;
      margin: 0 auto;
    }

    @media (max-width: 700px) {
      .recent-content {
        --padding-bottom: calc(120px + env(safe-area-inset-bottom));
      }

      .summary-row,
      .detail-grid,
      .order-card {
        grid-template-columns: 1fr;
      }

      .order-status,
      .order-money {
        justify-items: start;
      }

      .detail-actions {
        grid-template-columns: 1fr;
      }
    }

    @media print {
      ion-header,
      ion-content,
      ion-modal,
      ion-footer {
        display: none !important;
      }

      .print-receipt {
        background: #fff;
        color: #111;
        display: block !important;
        font-family: Arial, sans-serif;
        inset: 0;
        padding: 0;
        position: fixed;
        z-index: 9999;
      }

      .receipt-paper {
        display: grid;
        gap: 12px;
        margin: 0 auto;
        max-width: 320px;
        padding: 12px;
      }

      .receipt-header,
      .receipt-footer {
        display: grid;
        gap: 4px;
        text-align: center;
      }

      .receipt-header strong {
        font-size: 18px;
        text-transform: uppercase;
      }

      .receipt-header span,
      .receipt-block span,
      .receipt-block small,
      .receipt-line span,
      .receipt-line small,
      .receipt-payments span,
      .receipt-footer span {
        color: #444;
        font-size: 12px;
      }

      .receipt-folio,
      .receipt-block,
      .receipt-items,
      .receipt-totals,
      .receipt-payments {
        border-top: 1px dashed #777;
        display: grid;
        gap: 8px;
        padding-top: 10px;
      }

      .receipt-folio {
        grid-template-columns: auto 1fr;
      }

      .receipt-folio strong {
        text-align: right;
      }

      .receipt-items h2,
      .receipt-payments h2 {
        font-size: 13px;
        margin: 0;
        text-transform: uppercase;
      }

      .receipt-line,
      .receipt-totals div,
      .receipt-payments div {
        align-items: start;
        display: grid;
        gap: 10px;
        grid-template-columns: minmax(0, 1fr) auto;
      }

      .receipt-line strong,
      .receipt-line span,
      .receipt-line small {
        display: block;
      }

      .receipt-line > strong,
      .receipt-totals strong,
      .receipt-payments strong {
        text-align: right;
      }

      .receipt-totals {
        font-size: 13px;
      }

      .receipt-totals div:last-child {
        border-top: 1px solid #111;
        padding-top: 6px;
      }
    }
  `,
})
export class RecentOrdersPageComponent {
  readonly store = inject(PosStoreService);
  readonly printing = inject(PosPrintingService);
  readonly selectedOrder = signal<RecentPosOrder | null>(null);
  readonly detailOrder = signal<RecentPosOrder | null>(null);
  readonly detailOpen = signal(false);
  readonly paymentOpen = signal(false);
  readonly printableOrder = signal<RecentPosOrder | null>(null);
  readonly paymentMethod = signal<'cash' | 'transfer'>('cash');
  readonly paymentAmount = signal(0);

  constructor() {
    void this.store.loadRecentOrders();
  }

  openPayment(order: RecentPosOrder): void {
    this.selectedOrder.set(order);
    this.paymentAmount.set(order.balanceDue);
    this.paymentOpen.set(true);
  }

  openDetail(order: RecentPosOrder): void {
    this.detailOrder.set(order);
    this.detailOpen.set(true);
  }

  openPaymentFromDetail(order: RecentPosOrder): void {
    this.detailOpen.set(false);
    this.openPayment(order);
  }

  async printReceipt(order: RecentPosOrder): Promise<void> {
    try {
      const printed = await this.printing.printReceipt(order, {
        businessName: this.receiptBusinessName(),
        currency: this.store.selectedWorkspace().currency,
      });

      if (printed) {
        return;
      }
    } catch {
      // El servicio de impresion expone el error y mantenemos el fallback visual.
    }

    this.printableOrder.set(order);

    setTimeout(() => {
      const clearPrintableOrder = () => this.printableOrder.set(null);

      window.addEventListener('afterprint', clearPrintableOrder, { once: true });
      window.print();
    }, 80);
  }

  sourceEnabled(source: CommercialOrderSource): boolean {
    return this.store.recentSources().includes(source);
  }

  toggleSource(source: CommercialOrderSource, checked: boolean): void {
    void this.store.setRecentSource(source, checked);
  }

  sourceLabel(source: CommercialOrderSource): string {
    return {
      pos: 'POS',
      ecommerce: 'Ecommerce',
      panel: 'Panel',
    }[source];
  }

  async completePayment(): Promise<void> {
    const order = this.selectedOrder();

    if (!order) {
      return;
    }

    this.paymentOpen.set(false);

    try {
      await this.store.addOrderPayment(order, this.paymentAmount() || order.balanceDue, this.paymentMethod());
      this.selectedOrder.set(null);
      this.paymentAmount.set(0);
    } catch {
      this.paymentOpen.set(true);
    }
  }

  confirmedTotal(): number {
    return this.store
      .recentOrders()
      .filter((order) => order.paymentStatus === 'paid')
      .reduce((total, order) => total + order.paidTotal, 0);
  }

  pendingBalance(): number {
    return this.store
      .recentOrders()
      .reduce((total, order) => total + order.balanceDue, 0);
  }

  paymentLabel(status: string): string {
    return {
      paid: 'Pagado',
      partial: 'Parcial',
      unpaid: 'Sin pago',
    }[status] ?? status;
  }

  methodLabel(method: string): string {
    return {
      cash: 'Efectivo',
      transfer: 'Transferencia',
      card: 'Tarjeta',
      other: 'Otro',
    }[method] ?? method;
  }

  paymentStatusLabel(status: string): string {
    return {
      completed: 'Completado',
      pending: 'Pendiente',
      failed: 'Fallido',
      cancelled: 'Cancelado',
      paid: 'Pagado',
      partial: 'Parcial',
      unpaid: 'Sin pago',
    }[status] ?? status;
  }

  receiptBusinessName(): string {
    const workspace = this.store.selectedWorkspace();

    return workspace.posSettings?.posName || workspace.name;
  }

  addonPrice(addon: { price_delta?: number; priceDelta?: number }): number {
    return Number(addon.priceDelta ?? addon.price_delta ?? 0);
  }

  statusLabel(status: string): string {
    return {
      confirmed: 'Confirmado',
      quoted: 'Cotizado',
      cancelled: 'Cancelado',
    }[status] ?? status;
  }
}
